/** @jsx jsx */
import { useMemo } from 'react';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { FormSection } from '@atlaskit/form';
import Link from '@atlaskit/link';
import { token } from '@atlaskit/tokens';
import EditIcon from '@atlaskit/icon/core/edit';
import { useIntl } from '@atlassian/jira-intl';
import { ModalButtonTrigger } from '@atlassian/entry-point-modal-trigger/ModalButtonTrigger';
import {
	ApplicationClientId,
	ApplicationClientSecret,
	CopyableApplicationClientID,
	CopyableApplicationClientSecret,
	RedirectUrl,
	RemoteApplicationScopeMultiSelect,
} from '../../../common/fields/index.tsx';
import { getApplinksAdminUrl } from '../../../../common/interchange.tsx';
import { outgoingCredentialsModalEntryPoint } from '../outgoing-credentials-modal/entrypoint.tsx';
import messages from '../../messages.tsx';
import type {
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkV3Data,
} from '../../../../common/entities.tsx';

const styles = cssMap({
	growField: {
		flexGrow: 1,
	},
	formSectionInner: {
		marginTop: token('space.200'),
	},
});

const OutgoingCredentialsModalButton = (props: {
	applink: ApplicationLinkV3Data;
	onDisplayError: (err: string) => void;
	onRequestStatusRefresh: () => void;
}) => {
	const { formatMessage } = useIntl();

	const entryPointProps = useMemo(
		() => ({
			applink: props.applink,
			onDisplayError: props.onDisplayError,
			onClose: props.onRequestStatusRefresh,
		}),
		[props.applink, props.onDisplayError, props.onRequestStatusRefresh],
	);

	return (
		<ModalButtonTrigger
			testId="applinks-administration.ui.edit.oauth2.credentials-form.outgoing-credentials-modal-button"
			name="updateOutgoingLinkCredentials"
			entryPoint={outgoingCredentialsModalEntryPoint}
			entryPointProps={entryPointProps}
			iconBefore={EditIcon}
		>
			{formatMessage(messages.outgoingCredentialsButtonText)}
		</ModalButtonTrigger>
	);
};

type CredentialsFormProps = {
	applink: ApplicationLinkV3Data;
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo;
	partiallyLinkedMode?: boolean;
	onDisplayError: (err: string) => void;
	onRequestStatusRefresh: () => void;
};

const CredentialsForm = (props: CredentialsFormProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Stack>
			<FormSection
				title={formatMessage(messages.incomingLinkTitle)}
				description={formatMessage(messages.incomingLinkDescription)}
			>
				<Stack space="space.200" xcss={styles.formSectionInner}>
					<Inline space="space.200">
						<Box xcss={styles.growField}>
							<CopyableApplicationClientID
								name="copyableApplicationClientId"
								clientId={props.oauth2ConfigInfo.inboundOAuthClientId}
							/>
						</Box>
						<Box xcss={styles.growField}>
							<CopyableApplicationClientSecret
								name="copyableApplicationClientSecret"
								clientSecret={props.oauth2ConfigInfo.inboundOAuthClientSecret}
							/>
						</Box>
					</Inline>
				</Stack>
			</FormSection>
			<FormSection
				title={formatMessage(messages.outgoingLinkTitle)}
				description={formatMessage(messages.outgoingLinkDescription)}
			>
				<Stack space="space.200" xcss={styles.formSectionInner}>
					{props.partiallyLinkedMode && (
						<Stack space="space.200">
							<Inline space="space.200">
								<Box xcss={styles.growField}>
									<ApplicationClientId name="applicationClientId" />
								</Box>
								<Box xcss={styles.growField}>
									<ApplicationClientSecret name="applicationClientSecret" />
								</Box>
							</Inline>
						</Stack>
					)}
					{!props.oauth2ConfigInfo.isRemoteApplicationCloud && (
						<RedirectUrl
							name="redirectUrl"
							defaultRedirectUrl={props.oauth2ConfigInfo?.inboundRedirectUrl || ''}
						/>
					)}
					{!props.oauth2ConfigInfo.isRemoteApplicationCloud && (
						<RemoteApplicationScopeMultiSelect
							name="remoteApplicationSupportedScopes"
							supportedScopes={props.oauth2ConfigInfo.remoteApplicationSupportedScopes || []}
							defaultSelected={
								props.oauth2ConfigInfo.outboundScopes?.map((scope) => ({
									label: scope,
									value: scope,
								})) || []
							}
						/>
					)}
					{!props.partiallyLinkedMode && (
						<Box>
							<OutgoingCredentialsModalButton
								applink={props.applink}
								onDisplayError={props.onDisplayError}
								onRequestStatusRefresh={props.onRequestStatusRefresh}
							/>
						</Box>
					)}
					<Link
						href={getApplinksAdminUrl(props.applink.displayUrl, props.applink.type)}
						target="_blank"
					>
						{formatMessage(messages.goToRemoteSiteLink, {
							displayUrl: props.applink.displayUrl,
						})}
					</Link>
				</Stack>
			</FormSection>
		</Stack>
	);
};

export default CredentialsForm;
